import { Box, useTheme, InputBase, Popper, Paper } from "@mui/material";
import {
  DataGrid,
  GridToolbar,
  gridClasses,
  useGridApiContext,
} from "@mui/x-data-grid";
import { tokens } from "../../theme";
import Headers from "../../components/Headers";
import { useTemplateStore, useLoginStore } from "../../Utils/Store";
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
} from "react";
import TemplatesActions from "./TemplatesActions";

const Templates = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const templateCreated = useTemplateStore((state) => state.templateCreated);
  const getTemplates = useTemplateStore((state) => state.getTemplates);
  const [rowId, setRowId] = useState(null);
  const [newTemplates, setNewTemplates] = useState([]);

  const isLogin = useLoginStore((state) => state.isLogin);

  useEffect(() => {
    getTemplates();
  }, []);

  useEffect(() => {
    if (isLogin && templateCreated) {
      setNewTemplates(templateCreated);
    }
  }, [templateCreated, isLogin]);

  function EditTextarea(props) {
    const { id, field, value, colDef, hasFocus } = props;
    const [valueState, setValueState] = useState(value);
    const [anchorEl, setAnchorEl] = useState();
    const [inputRef, setInputRef] = useState(null);
    const apiRef = useGridApiContext();

    useLayoutEffect(() => {
      if (hasFocus && inputRef) {
        inputRef.focus();
      }
    }, [hasFocus, inputRef]);

    const handleRef = useCallback((el) => {
      setAnchorEl(el);
    }, []);

    const handleChange = useCallback(
      (event) => {
        const newValue = event.target.value;
        setValueState(newValue);
        apiRef.current.setEditCellValue(
          { id, field, value: newValue, debounceMs: 200 },
          event
        );
      },
      [apiRef, field, id]
    );

    return (
      <div style={{ position: "relative", alignSelf: "flex-start" }}>
        <div
          ref={handleRef}
          style={{
            height: 1,
            width: colDef.computedWidth,
            display: "block",
            position: "absolute",
            top: 0,
          }}
        />
        {anchorEl && (
          <Popper open anchorEl={anchorEl} placement="bottom-start">
            <Paper elevation={1} sx={{ p: 1, minWidth: colDef.computedWidth }}>
              <InputBase
                multiline
                rows={4}
                value={valueState}
                sx={{ textarea: { resize: "both" }, width: "100%" }}
                onChange={handleChange}
                inputRef={(ref) => setInputRef(ref)}
              />
            </Paper>
          </Popper>
        )}
      </div>
    );
  }

  const multilineColumn = useMemo(
    () => ({
      type: "string",
      renderEditCell: (params) => <EditTextarea {...params} />,
    }),
    []
  );

  function getTemplateName(params) {
    if (!params || !params.row) return '';
    const user = params.row.user;
    return user ? user.username || "" : "";
  }

  const columns = useMemo(
    () => [
      { field: "id", headerName: "ID" },
      {
        field: "description",
        headerName: "Description",
        flex: 1,
        ...multilineColumn,
        editable:
          isLogin?.access_level === "Manager" ||
          isLogin?.access_level === "Admin",
      },
      {
        field: "created-by",
        headerName: "Created by",
        cellClassName: "name-column--cell",
        flex: 1,
        valueGetter: getTemplateName,
      },
      {
        field: "date",
        headerName: "Date",
        flex: 1,
      },
      {
        field: "actions",
        headerName: "Actions",
        type: "actions",
        flex: 1,
        renderCell: (params) => (
          <TemplatesActions {...{ params, rowId, setRowId }} />
        ),
      },
    ],
    [rowId, multilineColumn, isLogin?.access_level]
  );

  return (
    <Box m="20px">
      <Paper
        elevation={0}
        sx={{ borderRadius: "10px", background: colors.primary[400] }}
      >
        <Headers title="Templates" />
      </Paper>
      <Paper
        elevation={0}
        sx={{ borderRadius: "10px", background: colors.primary[400] }}
      >
        <Box
          m="10px 0 0 0"
          height="70vh"
          sx={{
            "& .MuiDataGrid-root": {
              border: "none",
            },
            "& .MuiDataGrid-cell": {
              borderBottom: "none",
              whiteSpace: "pre-wrap",
              wordWrap: "break-word",
              maxHeight: "none",
              fontSize: "14px",
            },
            "& .name-column--cell": {
              color: colors.greenAccent[300],
            },
            "& .MuiDataGrid-columnHeaders": {
              backgroundColor: colors.blueAccent[700],
              borderBottom: "none",
              fontSize: "16px",
            },
            "& .MuiDataGrid-virtualScroller": {
              backgroundColor: colors.primary[400],
            },
            "& .MuiDataGrid-footerContainer": {
              borderTop: "none",
              backgroundColor: colors.blueAccent[700],
            },
            "& .MuiDataGrid-toolbarContainer .MuiButton-text": {
              color: `${colors.grey[100]} !important`,
            },
          }}
        >
          <DataGrid
            rows={newTemplates}
            columns={columns}
            getRowId={(row) => row.id}
            onCellEditCommit={(params) => setRowId(params.id)}
            components={{ Toolbar: GridToolbar }}
            getRowHeight={() => "auto"}
            sx={{
              [`& .${gridClasses.cell}`]: {
                py: 1,
              },
            }}
          />
        </Box>
      </Paper>
    </Box>
  );
};

export default Templates;
